import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_AUTISM_CHILDREN,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import {
  convertToLowerCase,
  getAutismChildPageImage,
} from "../../utils/utility-functions";
import Header from "../../components/header";
import BackToTopButton from "../../components/back-to-top/component";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import PathHelper from "../../utils/path-helper";
import {
  AUTISM_CHILDREN_OVERALL_RANGES,
  AUTISM_CHILDREN_SECTION_RANGES,
  autismChildrenScore,
  getLevelByRanges,
} from "../../utils/assessment-utils";
import ExpandableSectionV2 from "../../components/expandable-collapsible-v2/";

const AutismChildReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
  getAssessmentAttemptConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const [questionSetType, setQuestionSetType] = useState("");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const isChild = questionSetType === "child";

  const scoreGaugeTier = isChild ? "green" : "orange";
  const resultTextColor = isChild ? "#53AF65" : "#E0791F";
  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true);

      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );

        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch question set type
        const questionSetTypeResponse = await getAssessmentAttemptConnect(
          userId,
          assessmentAttemptId,
        );

        if (!questionSetTypeResponse?.questionSetType) {
          throw new Error("Question set type not found.");
        }

        const selectedQuestionSetType = convertToLowerCase(
          questionSetTypeResponse.questionSetType,
        );

        setQuestionSetType(selectedQuestionSetType);

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_AUTISM_CHILDREN[currentLocale] ||
            MODEL_TYPE_AUTISM_CHILDREN[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (!contentResponse?.content?.variants?.[selectedQuestionSetType]) {
          throw new Error(
            `Content not found for question set type: ${selectedQuestionSetType}`,
          );
        }

        setContentData(
          contentResponse.content.variants[selectedQuestionSetType],
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        // Optionally display a toast or error message here
        // toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale, userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  const result1ImageText = "act-result-1";
  const result1Image = getAutismChildPageImage(result1ImageText);
  const result2ImageText = "act-result-2";
  const result2Image = getAutismChildPageImage(result2ImageText);
  const result3ImageText = "act-result-3";
  const result3Image = getAutismChildPageImage(result3ImageText);
  const traitScore = autismChildrenScore(userTraits, currentLocale);
  const resultImage = questionSetType === "teen" ? result1Image : result3Image;
  const MAX_OVERALL_SCORE = 105;
  const MAX_SECTION_SCORE = 15;

  const overAllScoreLevel = getLevelByRanges(
    traitScore?.overallScore,
    AUTISM_CHILDREN_OVERALL_RANGES,
  );

  const TakeTestButton = ({ onClick, id }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id={id}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const colorVariants = {
    violet: {
      from: "rgb(106, 35, 201)", // #6A23C9
      to: "rgb(178, 75, 214)", // #B24BD6
    },
    blue: {
      from: "#26307F",
      to: "#3F63D8",
    },
    green: {
      from: "#146C43",
      to: "#2FAE66",
    },
    red: {
      from: "#C0392B",
      to: "#E0685A",
    },
    orange: {
      from: "#C2410C",
      to: "#F97316",
    },
    teal: {
      from: "#0F766E",
      to: "#14B8A6",
    },
    pink: {
      from: "#BE185D",
      to: "#EC4899",
    },
    indigo: {
      from: "#3730A3",
      to: "#6366F1",
    },
    cyan: {
      from: "#0F766E",
      to: "#06B6D4",
    },
    yellow: {
      from: "#B45309",
      to: "#FACC15",
    },
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const bannerColors = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className="relative h-auto overflow-hidden"
        style={{
          background: `
          radial-gradient(1.6px 1.6px at 40px 26px, rgba(255,255,255,0.40), transparent),
          radial-gradient(1.4px 1.4px at 160px 70px, rgba(255,255,255,0.28), transparent),
          radial-gradient(1.1px 1.1px at 300px 34px, rgba(255,255,255,0.30), transparent),
          radial-gradient(1.5px 1.5px at 460px 84px, rgba(255,255,255,0.25), transparent),
          radial-gradient(1px 1px at 600px 48px, rgba(255,255,255,0.30), transparent),
          radial-gradient(1.2px 1.2px at 760px 32px, rgba(255,255,255,0.25), transparent),
          radial-gradient(1.3px 1.3px at 920px 76px, rgba(255,255,255,0.35), transparent),
          linear-gradient(105deg, ${bannerColors.from}, ${bannerColors.to})
        `,
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/10" />

        {/* Left Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(
            to right,
            ${bannerColors.from}DD 0%,
            ${bannerColors.from}88 35%,
            transparent 75%
          )`,
          }}
        />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-4 lg:px-8 lg:py-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-4 md:pr-16">
              <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                <h2
                  id={reportType}
                  className="text-left text-[27px] font-bold text-white break-words whitespace-pre-wrap"
                >
                  {title}
                </h2>
              </div>

              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-enneagram-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              id={"likeability-report-take-disc-test-cta"}
              s
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  const TRAIT_CONFIG = {
    social_communication: {
      color: "blue",
    },
    social_reciprocity: {
      color: "purple",
    },
    communication_style: { color: "teal" },
    routines_and_repetition: {
      color: "amber",
    },
    interests: { color: "rose" },
    sensory: { color: "green" },
    coping: { color: "indigo" },
  };

  function mapTraitsToCategories(userTraits, breakdownData) {
    const traitOrder = Object.keys(TRAIT_CONFIG);

    const filteredTraits = userTraits.filter((trait) => {
      const exists = !!TRAIT_CONFIG[trait.title];
      return exists;
    });

    const sortedTraits = filteredTraits.sort(
      (a, b) => traitOrder.indexOf(a.title) - traitOrder.indexOf(b.title),
    );

    return sortedTraits.map((trait) => {
      const config = TRAIT_CONFIG[trait.title];

      const section = breakdownData?.[trait.title];

      const level = getLevelByRanges(
        trait.score,
        AUTISM_CHILDREN_SECTION_RANGES,
      );

      const band = section?.bands?.[level.toLowerCase()];

      const score = trait.score;

      const result = {
        title: section?.label,
        score,
        maxScore: MAX_SECTION_SCORE,
        level:
          band?.label ||
          (level
            ? level.charAt(0).toUpperCase() + level.slice(1).toLowerCase()
            : level),
        color: config?.color,
        subtitle: section?.subtitle,
        description: band?.description,
      };

      return result;
    });
  }
  const categories = mapTraitsToCategories(
    traitScore.sections,
    contentData?.detailedBreakdown?.sections,
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <div className="w-full max-w-6xl mx-auto py-6">
              {/* SECTION 1 - Child's Screening Results */}
              <section className="w-full bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full px-8 lg:px-10 flex justify-end gap-[10px]">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />

                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}

                  <div className="pb-8">
                    {/* Intro */}
                    <div className="pb-10 md:w-[83%]">
                      <p
                        className="text-[15.5px] leading-[1.75] font-normal text-[#4B5563]"
                        dangerouslySetInnerHTML={{
                          __html: contentData?.intro || "",
                        }}
                      />
                    </div>

                    {/* Score + Image */}
                    <div className="w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                        <div className="flex flex-col items-center">
                          <ScoreGauge
                            score={traitScore?.overallScore}
                            maxScore={MAX_OVERALL_SCORE}
                            label={
                              contentData?.overallResult?.bands?.[
                                overAllScoreLevel
                              ]?.label
                            }
                            tier={scoreGaugeTier}
                          />

                          <div className="mt-6 w-full">
                            <ScoresTable
                              rows={[
                                {
                                  label: t(
                                    "report.autism_children.total_score",
                                  ),
                                  value: `${traitScore.overallScore} / ${MAX_OVERALL_SCORE}`,
                                  tier: scoreGaugeTier,
                                },
                                {
                                  label: t(
                                    "report.autism_children.overall_result",
                                  ),
                                  value:
                                    contentData?.overallResult?.bands?.[
                                      overAllScoreLevel
                                    ]?.shortLabel,
                                  tier: scoreGaugeTier,
                                },
                              ]}
                            />
                          </div>
                        </div>

                        <div className="relative rounded-2xl overflow-hidden h-[320px] md:h-[520px] bg-[#F5F5F5] flex items-center justify-center">
                          <img
                            src={resultImage}
                            alt="Assessment Result"
                            className="w-[92%] h-[92%] object-contain"
                          />
                        </div>
                      </div>

                      {/* Heading */}
                      <h3 className="text-[18px] font-bold text-[#1F2937] mb-3 mt-6">
                        {contentData?.overallResult?.heading}
                      </h3>

                      {/* Result Card */}
                      <ResultMeaningCard
                        title={
                          contentData?.overallResult?.bands?.[overAllScoreLevel]
                            ?.label
                        }
                        description={
                          contentData?.overallResult?.bands?.[overAllScoreLevel]
                            ?.description
                        }
                        tier={scoreGaugeTier}
                        titleStyle={{
                          color: resultTextColor,
                        }}
                      />
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {/* SECTION 2 - How is this scored */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  heroBanner={
                    <HeroBanner
                      title={contentData?.detailedBreakdown?.heading}
                      color="violet"
                    />
                  }
                >
                  <div className="px-4 py-4">
                    <p className="text-gray-700 text-[15.5px] leading-relaxed mb-6">
                      {contentData?.detailedBreakdown?.intro}
                    </p>

                    {/* How is this scored callout */}
                    <div className="bg-[#F6F4FC] border-l-[4px] border-[#6B46E5] rounded-[10px] px-[18px] py-[14px] mb-6 leading-[1.8]">
                      <span className="font-bold text-[#5B3CC4] text-[14.5px]">
                        {contentData?.howScored?.heading}{" "}
                      </span>
                      <span className="text-[#4B5563] text-[15px]">
                        {contentData?.howScored?.content}
                      </span>
                    </div>

                    {/* Category grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {categories.map((cat, i) => (
                        <CategoryCard key={i} {...cat} />
                      ))}
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {/* SECTION 3 - Next Steps */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  heroBanner={
                    <HeroBanner
                      title={contentData?.howToUse?.heading}
                      color="red"
                    />
                  }
                >
                  <h2 className="text-[17px] font-bold text-gray-900 mb-4">
                    {contentData?.additionalInsights?.heading}
                  </h2>
                  <p
                    className="text-gray-700 text-[15.5px] leading-relaxed mb-6 w-[85%]"
                    dangerouslySetInnerHTML={{
                      __html: (
                        contentData?.additionalInsights?.bands?.[
                          overAllScoreLevel
                        ] || ""
                      ).replace(/\n/g, "<br />"),
                    }}
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="flex flex-col gap-6">
                      <Callout
                        title={contentData?.nextSteps?.heading}
                        icon
                        color="purpleSoft"
                      >
                        <ul className="list-disc pl-6 space-y-2 marker:text-[18px]">
                          {contentData?.nextSteps?.bands?.[
                            overAllScoreLevel
                          ].map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ul>
                      </Callout>

                      <Callout
                        title={contentData?.seekHelp?.heading}
                        color="amber"
                      >
                        <p>{contentData?.seekHelp?.base}</p>
                        <p className="mt-2">
                          {
                            contentData?.seekHelp?.addendums?.[
                              overAllScoreLevel
                            ]
                          }
                        </p>
                      </Callout>
                    </div>

                    <div className="relative rounded-2xl overflow-hidden bg-[#F5F5F5] min-h-[320px] flex items-center justify-center">
                      {/*Image*/}
                      <img
                        src={result2Image}
                        alt=""
                        className="absolute bottom-16 left-1/2 -translate-x-1/2 w-full p-8"
                      />
                    </div>
                  </div>

                  <Callout
                    title={contentData?.disclaimer?.heading}
                    color="greenSoft"
                  >
                    <p
                      dangerouslySetInnerHTML={{
                        __html: contentData?.disclaimer?.content || "",
                      }}
                    />
                  </Callout>

                  <div className="mt-8">
                    <ResourcesList
                      heading={contentData?.resources?.heading}
                      intro={contentData?.resources?.intro}
                      items={contentData?.resources?.items}
                    />
                  </div>
                </ExpandableSectionV2>
              </section>

              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.CUSTOM_BLUE}
                />
              ) : (
                CallToActionSectionForUnAuth({ contentData })
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AutismChildReport;

const TIER_STYLES = {
  green: "bg-green-50 border-green-500 text-green-700",
  yellow: "bg-yellow-50 border-yellow-500 text-yellow-700",
  red: "bg-red-50 border-red-500 text-red-700",
  orange:
    "bg-[rgb(253,243,230)] border border-[rgb(243,221,184)] text-[#E0791F] rounded-[14px]",
};

// const ResultMeaningCard = ({ title, description, tier = "green" }) => (
//   <div className={`w-full rounded-xl border p-6 ${TIER_STYLES[tier]}`}>
//     <h4 className="text-[17px] font-bold mb-2">{title}</h4>
//     <p className="text-gray-600 text-[15px] leading-relaxed">{description}</p>
//   </div>
// );

const ResultMeaningCard = ({
  title,
  description,
  tier = "green",
  titleStyle,
}) => (
  <div
    className={`${TIER_STYLES[tier]}`}
    style={{
      borderRadius: "14px",
      padding: "20px 22px",
    }}
  >
    <h4
      className="mb-2"
      style={{
        marginTop: "2px",
        fontFamily: "Poppins, sans-serif",
        fontWeight: 600,
        fontSize: "19px",
        ...titleStyle,
      }}
    >
      {title}
    </h4>

    <p className="text-gray-600 text-[15px] leading-relaxed">{description}</p>
  </div>
);

const TIER_TEXT = {
  green: "text-green-600",
  yellow: "text-yellow-600",
  red: "text-red-600",
  orange: "text-[#E0791F]",
};

const ScoresTable = ({ rows }) => {
  const { t } = useTranslation();

  return (
    <div className="w-full mt-8">
      <div className="grid grid-cols-[1fr_auto] items-center pb-4 border-b border-gray-200">
        <span className="text-[16px] font-bold text-[#111827]">
          {t("report.autism_children.your_scores")}
        </span>
        <span className="text-[16px] font-bold text-[#111827] text-right">
          {t("report.autism_children.results")}
        </span>
      </div>

      {/* Rows */}
      {rows.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_auto] items-center py-6 border-b border-gray-200 last:border-b-0"
        >
          <span className="text-[15.5px] text-[#374151]">{row.label}</span>

          <span
            className={`text-[15.5px] font-semibold text-right ${
              TIER_TEXT[row.tier] || "text-[#111827]"
            }`}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
};
const TIER_COLORS = {
  green: {
    stroke: "#37C978",
    text: "text-[#249D5C]",
  },
  yellow: {
    stroke: "#F6C343",
    text: "text-[#D89E00]",
  },
  red: {
    stroke: "#F25555",
    text: "text-[#E53935]",
  },
  orange: {
    stroke: "#E0791F",
    text: "text-[#E0791F]",
  },
};

const ScoreGauge = ({ score, maxScore, label, tier = "green" }) => {
  const { t } = useTranslation();
  const { stroke, text } = TIER_COLORS[tier];

  const radius = 140;
  const circumference = Math.PI * radius;
  const percentage = Math.min(Math.max(score / maxScore, 0), 1);
  const dashOffset = circumference * (1 - percentage);

  return (
    <div className="flex flex-col items-center w-full max-w-[360px]">
      <div className="relative w-full">
        <svg viewBox="0 0 300 185" className="w-full">
          {/* Background */}
          <path
            d="M30 150 A120 120 0 0 1 270 150"
            fill="none"
            stroke="#EEF0F8"
            strokeWidth="20"
            strokeLinecap="round"
          />

          {/* Progress */}
          <path
            d="M30 150 A120 120 0 0 1 270 150"
            fill="none"
            stroke={stroke}
            strokeWidth="20"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{
              transition: "stroke-dashoffset .7s ease",
            }}
          />
        </svg>

        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
          <div className={`text-[56px] font-extrabold leading-none ${text}`}>
            {score}
          </div>

          <div className="mt-2 text-[16px] font-semibold text-[#8B8B99]">
            {t("report.autism_children.out_of")} {maxScore}
          </div>
        </div>

        {/* LOW / HIGH */}
        <div className="absolute bottom-2 left-6 right-6 flex justify-between text-[#8B8B99] text-[12px] font-[700]">
          <span>{t("report.autism_children.low")}</span>
          <span>{t("report.autism_children.high")}</span>
        </div>
      </div>

      {/* Bottom Label */}
      <h2 className={`mt-4 text-xl font-bold text-center ${text}`}>{label}</h2>
    </div>
  );
};

const THEME = {
  blue: {
    bg: "bg-[#ECEFFF]",
    bar: "bg-[#4F46E5]",
    badge: "text-[#4338CA]",
    label: "text-[#4338CA]",
    text: "text-[#3730A3]",
    border: "border border-[#D6DCFF]",
  },
  purple: {
    bg: "bg-[#F4ECFF]",
    bar: "bg-[#7C3AED]",
    badge: "text-[#6D28D9]",
    label: "text-[#6D28D9]",
    text: "text-[#5B21B6]",
    border: "border border-[#E4D5FB]",
  },
  purpleSoft: {
    bg: "bg-[#F4ECFF]",
    border: "border border-[#E4D5FB]",
    title: "text-[#6C4CF5]",
    badge: "text-[#6C4CF5]",
    label: "text-[#6C4CF5]",
  },
  teal: {
    bg: "bg-[#E7F6F4]",
    bar: "bg-[#0F766E]",
    badge: "text-[#0F766E]",
    label: "text-[#0F766E]",
    text: "text-[#115E59]",
    border: "border border-[#BFE8E3]",
  },
  amber: {
    bg: "bg-[#FDF5E6]",
    bar: "bg-amber-600",
    border: "border border-[#F1E2C2]",
    badge: "text-amber-700",
    label: "text-amber-700",
    title: "text-amber-700",
  },
  creamSoft: {
    bg: "bg-[#FDF5E6]",
    bar: "bg-[#D97706]",
    badge: "text-[#B45309]",
    label: "text-[#B45309]",
    text: "text-[#92400E]",
    border: "border border-[#F1E2C2]",
  },
  rose: {
    bg: "bg-rose-50",
    bar: "bg-rose-600",
    badge: "text-rose-700",
    label: "text-rose-700",
    text: "text-rose-800",
    border: "border-rose-200",
  },
  green: {
    bg: "bg-green-50",
    bar: "bg-green-600",
    badge: "text-green-700",
    label: "text-green-700",
    text: "text-green-800",
    border: "border-green-200",
  },
  greenSoft: {
    bg: "bg-[#EAF6EE]",
    border: "border border-[#CFE9DA]",
    title: "text-[#2E7D5A]",
    badge: "text-[#2E7D5A]",
    label: "text-[#2E7D5A]",
  },
  indigo: {
    bg: "bg-[#EEF2FF]",
    bar: "bg-[#4F46E5]",
    badge: "text-[#4338CA]",
    label: "text-[#4338CA]",
    text: "text-[#3730A3]",
    border: "border border-[#C7D2FE]",
  },
};
const CategoryCard = ({
  title,
  score,
  maxScore,
  level,
  subtitle,
  description,
  color = "blue",
}) => {
  const theme = THEME[color] || THEME.blue;
  const percentage = Math.min(Math.max((score / maxScore) * 100, 0), 100);

  return (
    <div className={`rounded-2xl p-6 ${theme.bg}`}>
      <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
        <h3 className="flex-1 min-w-0 break-words text-[16px] font-bold text-gray-900">
          {title}
        </h3>
        <span
          className={`flex-shrink-0 whitespace-nowrap bg-white rounded-full px-3 py-1 text-[12.5px] font-semibold ${theme.badge}`}
        >
          {score} / {maxScore} · {level}
        </span>
      </div>
      <div className="w-full h-[10px] bg-white/70 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full ${theme.bar} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className={`font-bold mb-2 text-[16px] ${theme.label}`}>{subtitle}</p>
      <p className="text-gray-600 text-[14px] leading-relaxed">{description}</p>
    </div>
  );
};

const Callout = ({ title, icon, color = "purple", border = "", children }) => {
  const theme = THEME[color] || THEME.purple;

  return (
    <div className={`rounded-2xl p-6 ${theme.bg} ${theme.border} ${border}`}>
      <div className="flex items-center gap-3 mb-4">
        {icon && (
          <span className="w-10 h-10 rounded-full bg-[#6C4CF5] flex items-center justify-center shrink-0">
            {/* icon */}
            <span className="w-[26px] h-[26px] rounded-full flex items-center justify-center shrink-0">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Longer horizontal line */}
                <path
                  d="M4 12H18"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />

                {/* Arrow head */}
                <path
                  d="M14 8L18 12L14 16"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        )}

        <h3
          className={`text-[16px] font-bold ${theme.title || "text-indigo-700"}`}
        >
          {title}
        </h3>
      </div>

      <div
        className={`text-[14.5px] leading-relaxed ${theme.text || "text-gray-700"}`}
      >
        {children}
      </div>
    </div>
  );
};

const ResourcesList = ({ heading, intro, items = [] }) => (
  <div>
    <h2 className="mb-3 text-lg font-bold text-gray-900">{heading}</h2>

    <p className="mb-2 text-[15px] text-gray-700">{intro}</p>

    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex text-gray-700  text-[15px]">
          <span className="mr-2 text-lg font-bold leading-6">•</span>
          <span>
            {item.label}
            {item.source && (
              <>
                {" — "}
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline hover:text-indigo-700"
                >
                  {item.source}
                </a>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  </div>
);
